import React, { useRef } from "react";
import { ArrowDown, ArrowRight, ArrowUpRight, Github, Linkedin, Mail } from "lucide-react";
import Card, { Kicker, Chip } from "../brand/Surface";
import Action from "../brand/Action";
import Reveal from "../brand/Reveal";
import Quote from "../brand/Quote";
import Hl from "../brand/Hl";
import { useCountUp, useTilt } from "../../lib/interactive";
import { gsap, useGSAP, reduced, whenFontsReady } from "../../lib/motion";
import { ME, HEADLINE, WORK, FACTS, SKILLS, period } from "../../data/cv";

const HATS = { 1: "Eén", 2: "Twee", 3: "Drie", 4: "Vier", 5: "Vijf" };

/** Everything in the strip, in one flat list. */
const TICKER = SKILLS.flatMap((g) => g.items.map((i) => i.name));

/**
 * A word that rises out of a mask.
 *
 * The mask is a separate element from the thing that moves, because the thing
 * that moves is sometimes a skewed `.hl` slab — and a slab that both leans and
 * translates inside its own overflow box clips its own corners off.
 */
function Word({ children }) {
  return (
    <span className="inline-block overflow-hidden py-[0.12em] align-bottom">
      <span className="rise inline-block">{children}</span>
    </span>
  );
}

export default function Landing() {
  const hero = useRef(null);

  useGSAP(
    () => {
      if (reduced()) return;

      /* Nothing is measured here, but the words are laid out by Unbounded and
         a hero that animates against fallback metrics jumps when the real face
         lands. Waiting costs nothing on a warm cache. */
      whenFontsReady().then(() => {
        if (!hero.current?.isConnected) return;

        const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

        /* Short. The whole entrance is over inside 700ms — a hero that takes
           two seconds to assemble itself is a hero nobody waits for. */
        tl.fromTo(".rise", { yPercent: 115 }, { yPercent: 0, duration: 0.5, stagger: 0.035 }, 0).fromTo(
          ".lift",
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, stagger: 0.05 },
          0.16,
        );
      });
    },
    { scope: hero },
  );

  return (
    <>
      {/* ------------------------------------------------------------ hero */}
      <section
        ref={hero}
        /* Full viewport minus the fixed chrome the shell already reserves, so
           the scroll hint lands on the fold rather than under it. */
        className="relative flex min-h-[calc(100svh-170px)] flex-col justify-center desk:min-h-[calc(100svh-152px)]"
      >
        <span className="kicker lift">{HEADLINE.kicker}</span>

        {/* Sized off the viewport width so it fills the screen at every size
            and still breaks where it should. */}
        <h1 className="mt-7 max-w-[17ch] text-[clamp(38px,7.6vw,100px)] leading-[0.95]">
          <Word>Ik</Word> <Word>bouw</Word> <Word>dingen</Word> <Word>die</Word>{" "}
          <Word>
            <Hl>blijven</Hl>
          </Word>{" "}
          <Word>
            <Hl>werken.</Hl>
          </Word>
        </h1>

        <p className="lift mt-8 max-w-[46ch] text-[16px] desk:text-[18px]">{HEADLINE.sub}</p>

        <div className="lift mt-10 flex flex-wrap items-center gap-3">
          <Action to="/projects" variant="accent" size="lg">
            Bekijk mijn werk
            <ArrowRight
              aria-hidden
              className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </Action>
          <Action to="/contact" variant="ghost" size="lg">
            Even praten
          </Action>

          <div className="ml-auto flex items-center gap-1">
            {[
              { href: ME.github, icon: Github, label: "GitHub" },
              { href: ME.linkedin, icon: Linkedin, label: "LinkedIn" },
              { href: `mailto:${ME.email}`, icon: Mail, label: "Mail" },
            ].map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer noopener"
                aria-label={label}
                className="tap grid size-11 place-items-center rounded-full text-muted transition-colors hover:text-accent-text"
              >
                <Icon aria-hidden className="size-[18px]" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        {/* The endless strip: what he actually works with, moving slowly. */}
        <div
          aria-hidden
          className="lift relative mt-14 overflow-hidden border-y border-hair py-3.5 [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]"
        >
          <div className="marquee gap-8">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex shrink-0 items-center gap-8 pr-8">
                {TICKER.map((item) => (
                  <span key={item} className="kicker whitespace-nowrap">
                    {item}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <a
          href="#werk"
          className="kicker tap lift mt-10 inline-flex w-fit items-center gap-2 py-2 transition-colors hover:text-fg"
        >
          <ArrowDown aria-hidden className="size-3.5 animate-bounce" strokeWidth={2} />
          Waar ik werk
        </a>
      </section>

      {/* The line that lights up on the way down. */}
      <Quote />

      {/* ------------------------------------------------------- the jobs */}
      <section id="werk" className="scroll-mt-28">
        <div className="mb-6">
          <Kicker>Nu</Kicker>
          {/* Counted, not typed. "Twee petten" survived exactly as long as it
              took to add a third. */}
          <h2 className="max-w-[18ch]">{HATS[WORK.length] ?? WORK.length} petten, tegelijk.</h2>
        </div>

        <Reveal stagger className="grid gap-4 sm:grid-cols-2 desk:grid-cols-3">
          {WORK.map((job) => (
            <JobCard key={job.org} job={job} />
          ))}
        </Reveal>
      </section>

      {/* ---------------------------------------------------------- facts */}
      <Reveal className="mt-4">
        <Card pad="lg" lit>
          <Kicker>Kort samengevat</Kicker>
          {/* Three across even on a phone: short values, and a stacked column
              of three numbers wastes a whole screen. */}
          <dl className="grid grid-cols-3 gap-3 desk:gap-6">
            {FACTS.map((f) => (
              <Fact key={f.label} value={f.value} label={f.label} />
            ))}
          </dl>
        </Card>
      </Reveal>
    </>
  );
}

function Fact({ value, label }) {
  const ref = useCountUp(value);
  return (
    <div>
      <dd
        ref={ref}
        className="nums font-display text-[30px] leading-none font-bold tracking-[-0.06em] text-accent-text desk:text-[46px]"
      >
        {value}
      </dd>
      <dt className="kicker mt-2.5">{label}</dt>
    </div>
  );
}

function JobCard({ job }) {
  const ref = useTilt();

  return (
    <Card
      ref={ref}
      pad="lg"
      className="tilt spot flex h-full flex-col transition-colors duration-300 hover:border-edge"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="accent">{job.kind}</Chip>
        <span className="kicker ml-auto normal-case">{period(job)}</span>
      </div>

      <h3 className="mt-6 font-display text-[28px] leading-none font-medium tracking-[-0.06em] lowercase">
        {job.org}
      </h3>
      <p className="mt-2.5 font-body text-[14.5px] font-bold text-fg">{job.role}</p>
      <p className="mt-3 text-[13.5px]">{job.line}</p>

      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-7">
        {job.tags.map((t) => (
          <Chip key={t} tone="outline">
            {t}
          </Chip>
        ))}
        {job.href && (
          <a
            href={job.href}
            target="_blank"
            rel="noreferrer noopener"
            className="pull tap ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent-text"
          >
            Bezoek
            <ArrowUpRight aria-hidden className="size-3.5" strokeWidth={2} />
          </a>
        )}
      </div>
    </Card>
  );
}
